import { type ButtonHTMLAttributes } from "react";
import { cn } from "../../lib/utils.ts";

type Variant = "primary" | "secondary" | "ghost" | "danger" | "heritage" | "heritage-outline";
type Size = "sm" | "md" | "lg" | "xl";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
}

const variantStyles: Record<Variant, string> = {
  primary:
    "bg-brand-600 text-white hover:bg-brand-700 active:bg-brand-800",
  secondary:
    "bg-slate-100 text-slate-900 hover:bg-slate-200 active:bg-slate-300",
  ghost:
    "bg-transparent text-slate-600 hover:bg-slate-100 active:bg-slate-200 underline",
  danger:
    "bg-error text-white hover:bg-red-700 active:bg-red-800",
  /* FamilyWoven: Heritage Variants */
  heritage:
    "bg-heritage-sage text-heritage-cream hover:bg-heritage-walnut transition-all duration-300 shadow-sm hover:shadow-md",
  "heritage-outline":
    "bg-transparent text-heritage-walnut border border-heritage-linen hover:bg-heritage-linen active:bg-heritage-linen/50 transition-all",
};

const sizeStyles: Record<Size, string> = {
  sm: "px-3 py-1.5 text-xs",
  md: "px-4 py-2 text-sm",
  lg: "px-6 py-3 text-base",
  xl: "px-10 py-4 text-sm uppercase tracking-widest font-bold",
};

export function Button({
  variant = "primary",
  size = "md",
  loading = false,
  disabled,
  className,
  children,
  ...props
}: ButtonProps) {
  return (
    <button
      className={cn(
        "inline-flex items-center justify-center font-sans font-medium transition-all",
        "focus:outline-none focus:ring-2 focus:ring-heritage-sage/30 focus:ring-offset-2",
        "disabled:opacity-50 disabled:cursor-not-allowed rounded-full",
        variantStyles[variant],
        sizeStyles[size],
        className,
      )}
      disabled={disabled || loading}
      {...props}
    >
      {loading && (
        <span className="mr-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
      )}
      {children}
    </button>
  );
}
